import { motion, useReducedMotionConfig, type Variants } from "motion/react";
import type { ReactNode } from "react";
import { cn } from "./cn";

/**
 * Esqueleto de carregamento animado, para conteúdo já posicionado (cartão,
 * lista, tabela). Equivalente livre do `loader-skeleton` do Motion UI, feito com
 * a biblioteca `motion` e os tokens do design system.
 *
 * Duas animações, de propósito separadas:
 *
 * - o bloco tem um brilho que atravessa da esquerda para a direita (ou pulso,
 *   se preferir algo mais discreto);
 * - dentro de `GrupoDeEsqueletos`, os blocos entram em sequência, de cima para
 *   baixo, em vez de aparecerem todos de uma vez.
 *
 * Quem pede movimento reduzido no sistema operacional recebe o bloco parado: o
 * brilho infinito é exatamente o tipo de animação que essa preferência existe
 * para evitar. `<MotionConfig reducedMotion="always">` força o mesmo no app
 * inteiro.
 */

export type EfeitoDoEsqueleto = "brilho" | "pulso";

const DURACAO_DO_BRILHO = 1.5;
const INTERVALO_ENTRE_BLOCOS = 0.08;

/**
 * Variantes de entrada. O bloco solto não tem `initial` nem `animate`, então
 * renderiza visível; dentro do grupo, herda o estado do pai e entra em sequência.
 */
const entradaDoBloco: Variants = {
  oculto: { opacity: 0, y: 4 },
  visivel: { opacity: 1, y: 0, transition: { duration: 0.25, ease: "easeOut" } },
};

export function Esqueleto({
  efeito = "brilho",
  className,
}: {
  readonly efeito?: EfeitoDoEsqueleto;
  readonly className?: string | undefined;
}) {
  const reduzirMovimento = useReducedMotionConfig() ?? false;
  const comBrilho = efeito === "brilho" && !reduzirMovimento;

  return (
    <motion.div
      aria-hidden="true"
      data-efeito={reduzirMovimento ? "parado" : efeito}
      variants={entradaDoBloco}
      className={cn(
        "relative overflow-hidden rounded-md bg-muted",
        efeito === "pulso" && !reduzirMovimento && "animate-pulse",
        className,
      )}
    >
      {comBrilho ? (
        <motion.span
          data-slot="brilho"
          className="absolute inset-0 bg-linear-to-r from-transparent via-foreground/8 to-transparent"
          initial={{ x: "-100%" }}
          animate={{ x: "100%" }}
          transition={{ duration: DURACAO_DO_BRILHO, ease: "linear", repeat: Infinity }}
        />
      ) : null}
    </motion.div>
  );
}

/**
 * Agrupa esqueletos: anuncia o carregamento para leitor de tela uma vez só e
 * faz os blocos filhos entrarem em sequência.
 */
export function GrupoDeEsqueletos({
  rotulo = "Carregando",
  children,
  className,
}: {
  readonly rotulo?: string;
  readonly children: ReactNode;
  readonly className?: string | undefined;
}) {
  const reduzirMovimento = useReducedMotionConfig() ?? false;

  return (
    <motion.div
      role="status"
      aria-live="polite"
      aria-busy="true"
      initial={reduzirMovimento ? false : "oculto"}
      animate="visivel"
      variants={{
        oculto: {},
        visivel: { transition: { staggerChildren: INTERVALO_ENTRE_BLOCOS } },
      }}
      className={className}
    >
      <span className="sr-only">{rotulo}</span>
      {children}
    </motion.div>
  );
}

/** Modelo pronto: cartão com avatar, título, duas linhas de texto e uma mídia. */
export function EsqueletoDeCartao({
  efeito,
  rotulo,
  className,
}: {
  readonly efeito?: EfeitoDoEsqueleto;
  readonly rotulo?: string;
  readonly className?: string | undefined;
}) {
  const bloco = efeito === undefined ? {} : { efeito };

  return (
    <GrupoDeEsqueletos
      {...(rotulo === undefined ? {} : { rotulo })}
      className={cn("space-y-4 rounded-3xl bg-card p-6 shadow-xl", className)}
    >
      <div className="flex items-center gap-3">
        <Esqueleto {...bloco} className="size-10 shrink-0 rounded-full" />
        <div className="flex-1 space-y-2">
          <Esqueleto {...bloco} className="h-4 w-1/3" />
          <Esqueleto {...bloco} className="h-3 w-1/4" />
        </div>
      </div>
      <Esqueleto {...bloco} className="h-3 w-full" />
      <Esqueleto {...bloco} className="h-3 w-5/6" />
      <Esqueleto {...bloco} className="aspect-video w-full rounded-xl" />
    </GrupoDeEsqueletos>
  );
}
